import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

const RECHARGE_PLAN_QUANTITY_MAP = new Map([
  ['gid://shopify/SellingPlan/20940373', 1],
  ['gid://shopify/SellingPlan/20940374', 3],
  ['gid://shopify/SellingPlan/20940375', 6],
  ['gid://shopify/SellingPlan/20940376', 12],
  ['20940373', 1],
  ['20940374', 3],
  ['20940375', 6],
  ['20940376', 12],
  ['gid://shopify/SellingPlan/3243868332', 1],
  ['gid://shopify/SellingPlan/3243901100', 3],
  ['gid://shopify/SellingPlan/3243933868', 6],
  ['gid://shopify/SellingPlan/3243966636', 12],
  ['3243868332', 1],
  ['3243901100', 3],
  ['3243933868', 6],
  ['3243966636', 12],
  ['gid://shopify/SellingPlan/3242590380', 1],
  ['gid://shopify/SellingPlan/3242655916', 3],
  ['gid://shopify/SellingPlan/3242721452', 6],
  ['gid://shopify/SellingPlan/3242786988', 12],
  ['3242590380', 1],
  ['3242655916', 3],
  ['3242721452', 6],
  ['3242786988', 12],
]);

const RECHARGE_PLAN_GID_PREFIX = 'gid://shopify/SellingPlan/';

/**
 * @param {string | undefined} planId
 * @returns {number | null}
 */
function getRechargePlanQuantity(planId) {
  if (!planId) return null;

  const raw = String(planId);
  if (RECHARGE_PLAN_QUANTITY_MAP.has(raw)) {
    return RECHARGE_PLAN_QUANTITY_MAP.get(raw) ?? null;
  }

  if (raw.startsWith(RECHARGE_PLAN_GID_PREFIX)) {
    const numeric = raw.slice(RECHARGE_PLAN_GID_PREFIX.length);
    return RECHARGE_PLAN_QUANTITY_MAP.get(numeric) ?? null;
  }

  if (/^\d+$/.test(raw)) {
    const gid = `${RECHARGE_PLAN_GID_PREFIX}${raw}`;
    return RECHARGE_PLAN_QUANTITY_MAP.get(gid) ?? null;
  }

  return null;
}

/**
 * @param {HTMLInputElement} input
 * @param {number} quantity
 * @returns {number}
 */
function clampQuantityToInput(input, quantity) {
  const min = parseInt(input.getAttribute('min') || '1', 10) || 1;
  const step = parseInt(input.getAttribute('step') || '1', 10) || 1;
  let value = Math.max(min, quantity);

  if ((value - min) % step !== 0) {
    value = min + Math.floor((value - min) / step) * step;
  }

  return value;
}

/**
 * @param {string | null | undefined} label
 * @returns {number | null}
 */
function parsePlanQuantityFromLabel(label) {
  if (!label) return null;
  const match = String(label).match(/(\d+)\s*[- ]?\s*(?:month|months|mo)\b/i);
  if (!match) return null;
  const value = parseInt(match[1], 10);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * @param {Document | ShadowRoot | HTMLElement} root
 * @returns {HTMLSelectElement | HTMLInputElement | null}
 */
function findRechargePlanElementInRoot(root) {
  if (!root) return null;

  const selectMatch = root.querySelector?.('select[name="rc_plan"]');
  if (selectMatch instanceof HTMLSelectElement) return selectMatch;

  const inputMatch = root.querySelector?.('input[name="rc_plan"]:not([type="hidden"])');
  if (inputMatch instanceof HTMLInputElement) return inputMatch;

  const sellingPlanMatch = root.querySelector?.('[name="selling_plan"]:not([type="hidden"])');
  if (sellingPlanMatch instanceof HTMLInputElement || sellingPlanMatch instanceof HTMLSelectElement) {
    return sellingPlanMatch;
  }

  const nodes = Array.from(root.querySelectorAll?.('*') || []);
  for (const node of nodes) {
    if (!(node instanceof HTMLElement)) continue;
    if (!node.shadowRoot) continue;
    const nestedMatch = findRechargePlanElementInRoot(node.shadowRoot);
    if (nestedMatch) return nestedMatch;
  }

  return null;
}

/**
 * @param {HTMLElement | null} element
 * @returns {string}
 */
function getPlanLabelFromElement(element) {
  if (!element) return '';

  if (element instanceof HTMLSelectElement) {
    const selectedOption = element.selectedOptions?.[0];
    return selectedOption?.textContent?.trim() || '';
  }

  if (element instanceof HTMLInputElement) {
    const ariaLabel = element.getAttribute('aria-label');
    if (ariaLabel) return ariaLabel.trim();

    const labelledBy = element.getAttribute('aria-labelledby');
    if (labelledBy) {
      const root = element.getRootNode();
      const labelEl =
        root instanceof Document || root instanceof ShadowRoot ? root.getElementById(labelledBy) : null;
      if (labelEl?.textContent) return labelEl.textContent.trim();
    }

    if (element.id) {
      const root = element.getRootNode();
      const labelEl =
        root instanceof Document || root instanceof ShadowRoot
          ? root.querySelector(`label[for="${CSS.escape(element.id)}"]`)
          : null;
      if (labelEl?.textContent) return labelEl.textContent.trim();
    }

    const parentLabel = element.closest('label');
    if (parentLabel?.textContent) return parentLabel.textContent.trim();

    if (element.parentElement?.textContent) {
      return element.parentElement.textContent.trim();
    }
  }

  return element.textContent?.trim() || '';
}

/**
 * @param {HTMLElement | null} element
 * @returns {number | null}
 */
function parsePlanQuantityFromElement(element) {
  if (!element) return null;

  const labelQuantity = parsePlanQuantityFromLabel(getPlanLabelFromElement(element));
  if (labelQuantity) return labelQuantity;

  const dataset = element.dataset || {};
  const rawFrequency =
    dataset.orderIntervalFrequency ||
    dataset.intervalFrequency ||
    dataset.frequency ||
    dataset.planIntervalFrequency ||
    '';
  const rawUnit =
    dataset.orderIntervalUnit ||
    dataset.intervalUnit ||
    dataset.unit ||
    dataset.planIntervalUnit ||
    '';

  const frequency = parseInt(rawFrequency, 10);
  if (!Number.isFinite(frequency) || frequency <= 0) return null;

  const unit = String(rawUnit).toLowerCase();
  if (unit.includes('month')) return frequency;

  return null;
}

/**
 * A custom element that allows the user to select a quantity.
 *
 * This component follows a pure event-driven architecture where quantity changes
 * are broadcast via QuantitySelectorUpdateEvent. Parent components that contain
 * quantity selectors listen for these events and handle them according to their
 * specific needs, with event filtering ensuring each parent only processes events
 * from its own quantity selectors to prevent conflicts between different cart
 * update strategies.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 *
 * @extends {Component<Refs>}
 */
export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];
  serverDisabledMinus = false;
  serverDisabledPlus = false;
  serverDisabledInput = false;
  initialized = false;
  rechargeForm = null;
  rechargePlanListener = null;
  rechargeObserver = null;
  rechargePlanElement = null;
  rechargePlanElementListener = null;
  rechargePlanRetryTimer = null;
  rechargePlanRetryCount = 0;
  rechargeWidgetHost = null;
  rechargeWidgetRoot = null;
  rechargeWidgetObserver = null;
  rechargeWidgetRootListener = null;

  connectedCallback() {
    super.connectedCallback();

    // Capture server-disabled state on first load
    const { minusButton, plusButton, quantityInput } = this.refs;

    if (minusButton.disabled) {
      this.serverDisabledMinus = true;
    }
    if (plusButton.disabled) {
      this.serverDisabledPlus = true;
    }
    if (quantityInput.disabled) {
      this.serverDisabledInput = true;
    }

    this.initialized = true;
    this.updateButtonStates();
    this.setupRechargePlanSync();
  }

  disconnectedCallback() {
    if (this.rechargeForm && this.rechargePlanListener) {
      this.rechargeForm.removeEventListener('change', this.rechargePlanListener);
      this.rechargeForm.removeEventListener('input', this.rechargePlanListener);
    }

    if (this.rechargeObserver) {
      this.rechargeObserver.disconnect();
    }

    if (this.rechargePlanElement && this.rechargePlanElementListener) {
      this.rechargePlanElement.removeEventListener('change', this.rechargePlanElementListener);
      this.rechargePlanElement.removeEventListener('input', this.rechargePlanElementListener);
    }

    if (this.rechargePlanRetryTimer) {
      clearInterval(this.rechargePlanRetryTimer);
    }

    this.rechargeForm = null;
    this.rechargePlanListener = null;
    this.rechargeObserver = null;
    this.rechargePlanElement = null;
    this.rechargePlanElementListener = null;
    this.rechargePlanRetryTimer = null;
    this.rechargePlanRetryCount = 0;
    this.rechargeWidgetHost = null;
    if (this.rechargeWidgetRootListener && this.rechargeWidgetRoot) {
      this.rechargeWidgetRoot.removeEventListener('change', this.rechargeWidgetRootListener, true);
      this.rechargeWidgetRoot.removeEventListener('input', this.rechargeWidgetRootListener, true);
    }
    this.rechargeWidgetRoot = null;
    this.rechargeWidgetRootListener = null;

    if (this.rechargeWidgetObserver) {
      this.rechargeWidgetObserver.disconnect();
      this.rechargeWidgetObserver = null;
    }

    super.disconnectedCallback();
  }

  /**
   * Updates cart quantity and refreshes component state
   * @param {number} cartQty - The quantity currently in cart for this variant
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    this.updateCartQuantity();
  }

  /**
   * Checks if the current quantity can be added to cart without exceeding max
   * @returns {{canAdd: boolean, maxQuantity: number|null, cartQuantity: number, quantityToAdd: number}} Validation result
   */
  canAddToCart() {
    const { max, cartQuantity, value } = this.getCurrentValues();
    const quantityToAdd = value;
    const wouldExceedMax = max !== null && cartQuantity + quantityToAdd > max;

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd,
    };
  }

  /**
   * Gets the current quantity value
   * @returns {string} The current value
   */
  getValue() {
    return this.refs.quantityInput.value;
  }

  /**
   * Sets the current quantity value
   * @param {string} value - The value to set
   */
  setValue(value) {
    this.refs.quantityInput.value = value;
  }

  /**
   * Updates min/max/step constraints and snaps value to valid increment
   * @param {string} min - Minimum value
   * @param {string|null} max - Maximum value (null if no max)
   * @param {string} step - Step increment
   */
  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;
    const currentValue = parseInt(quantityInput.value) || 0;

    quantityInput.min = min;
    if (max) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }
    quantityInput.step = step;

    const newMin = parseIntOrDefault(min, 1);
    const newStep = parseIntOrDefault(step, 1);
    const effectiveMax = this.getEffectiveMax();

    // Snap to valid increment if not already aligned
    let newValue = currentValue;
    if ((currentValue - newMin) % newStep !== 0) {
      // Snap DOWN to closest valid increment
      newValue = newMin + Math.floor((currentValue - newMin) / newStep) * newStep;
    }

    // Ensure value is within bounds
    newValue = Math.max(newMin, Math.min(effectiveMax ?? Infinity, newValue));

    if (newValue !== currentValue) {
      quantityInput.value = newValue.toString();
    }

    this.updateButtonStates();
  }

  /**
   * Gets current values from DOM (fresh read every time)
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      min: parseIntOrDefault(quantityInput.min, 1),
      max: parseIntOrDefault(quantityInput.max, null),
      step: parseIntOrDefault(quantityInput.step, 1),
      value: parseIntOrDefault(quantityInput.value, 0),
      cartQuantity: parseIntOrDefault(quantityInput.getAttribute('data-cart-quantity'), 0),
    };
  }

  /**
   * Gets the effective maximum value for this quantity selector
   * Product page: max - cartQuantity (how many can be added)
   * Override in subclass for different behavior
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max, cartQuantity, min } = this.getCurrentValues();
    if (max === null) return null;
    // Product page: can only add what's left
    return Math.max(max - cartQuantity, min);
  }

  /**
   * Updates button states based on current value and limits
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (this.isLocked()) {
      this.refs.quantityInput.readOnly = true;
      this.refs.quantityInput.setAttribute('aria-disabled', 'true');
      minusButton.disabled = true;
      plusButton.disabled = true;
      return;
    }

    // Only manage buttons that weren't server-disabled
    if (!this.serverDisabledMinus) {
      minusButton.disabled = value <= min;
    }

    if (!this.serverDisabledPlus) {
      plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }
  }

  /**
   * Updates quantity by a given step
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { min, step, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    const newValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value + step * stepMultiplier));

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(1);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(-1);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event (on blur).
   * Validates and snaps to valid values.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Snap to bounds
    const quantity = Math.min(effectiveMax ?? Infinity, Math.max(min, parseInt(event.target.value) || 0));

    // Validate step increment
    if ((quantity - min) % step !== 0) {
      // Set the invalid value and trigger native HTML validation
      quantityInput.value = quantity.toString();
      quantityInput.reportValidity();
      return;
    }

    quantityInput.value = quantity.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity change event.
   */
  onQuantityChange() {
    const { quantityInput } = this.refs;
    const newValue = parseInt(quantityInput.value);

    this.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine) || undefined));
  }

  /**
   * Checks if this quantity selector is locked.
   * @returns {boolean} Whether the selector is locked
   */
  isLocked() {
    return this.dataset.locked === 'true';
  }

  /**
   * Locks/unlocks the quantity selector UI.
   * @param {boolean} isLocked - Whether to lock the selector
   */
  setLocked(isLocked) {
    const { quantityInput, minusButton, plusButton } = this.refs;
    const locked = Boolean(isLocked);

    if (locked) {
      this.dataset.locked = 'true';
      quantityInput.readOnly = true;
      quantityInput.setAttribute('aria-disabled', 'true');
      minusButton.disabled = true;
      plusButton.disabled = true;
      return;
    }

    delete this.dataset.locked;
    quantityInput.readOnly = false;
    quantityInput.removeAttribute('aria-disabled');
    quantityInput.disabled = this.serverDisabledInput;
    this.updateButtonStates();
  }

  /**
   * Updates the cart quantity from data attribute and refreshes button states
   * Called when cart is updated from external sources
   */
  updateCartQuantity() {
    const { quantityInput } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Clamp value to new effective max if necessary
    const clampedValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value));

    if (clampedValue !== value) {
      quantityInput.value = clampedValue.toString();
    }

    this.updateButtonStates();
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }

  setupRechargePlanSync() {
    const { quantityInput } = this.refs;

    if (quantityInput.dataset.cartLine) return;

    const form = this.closest('form');
    if (!form) return;

    this.rechargeForm = form;

    this.rechargePlanListener = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!('name' in target)) return;
      if (target.name !== 'selling_plan' && target.name !== 'rc_plan') return;
      this.syncRechargeQuantity();
    };

    form.addEventListener('change', this.rechargePlanListener, true);
    form.addEventListener('input', this.rechargePlanListener, true);

    this.rechargeObserver = new MutationObserver(() => {
      this.syncRechargeQuantity();
    });

    this.rechargeObserver.observe(form, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['value', 'checked'],
    });

    this.rechargePlanElementListener = () => {
      this.syncRechargeQuantity();
    };

    this.attachRechargePlanElementListener();
    this.startRechargePlanRetry();
    this.syncRechargeQuantity();
  }

  startRechargePlanRetry() {
    if (this.rechargePlanRetryTimer) return;

    this.rechargePlanRetryCount = 0;
    this.rechargePlanRetryTimer = setInterval(() => {
      this.rechargePlanRetryCount += 1;
      this.attachRechargePlanElementListener();

      if (this.rechargePlanElement) {
        this.syncRechargeQuantity();
        clearInterval(this.rechargePlanRetryTimer);
        this.rechargePlanRetryTimer = null;
        return;
      }

      if (this.rechargePlanRetryCount >= 30) {
        clearInterval(this.rechargePlanRetryTimer);
        this.rechargePlanRetryTimer = null;
      }
    }, 500);
  }

  attachRechargePlanElementListener() {
    const planElement = this.getRechargePlanElement();
    if (!planElement || planElement === this.rechargePlanElement) return;

    if (this.rechargePlanElement && this.rechargePlanElementListener) {
      this.rechargePlanElement.removeEventListener('change', this.rechargePlanElementListener);
      this.rechargePlanElement.removeEventListener('input', this.rechargePlanElementListener);
    }

    this.rechargePlanElement = planElement;

    if (this.rechargePlanElementListener) {
      planElement.addEventListener('change', this.rechargePlanElementListener);
      planElement.addEventListener('input', this.rechargePlanElementListener);
    }
  }

  getRechargePlanElement() {
    const widgets = this.findRechargeWidgetHosts();
    for (const widget of widgets) {
      if (!widget.shadowRoot) continue;
      const planEl = findRechargePlanElementInRoot(widget.shadowRoot);
      if (planEl) {
        const root = planEl.getRootNode();
        this.setupRechargeWidgetObserver(widget, root instanceof ShadowRoot ? root : widget.shadowRoot);
        return planEl;
      }
    }

    if (this.rechargeForm) {
      const planElement = findRechargePlanElementInRoot(this.rechargeForm);
      if (planElement) return planElement;
    }

    return null;
  }

  setupRechargeWidgetObserver(widget, root = widget.shadowRoot) {
    if (widget === this.rechargeWidgetHost && root === this.rechargeWidgetRoot) return;

    if (this.rechargeWidgetObserver) {
      this.rechargeWidgetObserver.disconnect();
      this.rechargeWidgetObserver = null;
    }

    this.rechargeWidgetHost = widget;
    this.rechargeWidgetRoot = root;

    if (!root) return;

    if (this.rechargeWidgetRootListener && this.rechargeWidgetRoot) {
      this.rechargeWidgetRoot.removeEventListener('change', this.rechargeWidgetRootListener, true);
      this.rechargeWidgetRoot.removeEventListener('input', this.rechargeWidgetRootListener, true);
    }

    this.rechargeWidgetRootListener = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!('name' in target)) return;
      if (target.name !== 'selling_plan' && target.name !== 'rc_plan') return;
      this.syncRechargeQuantity();
    };

    this.rechargeWidgetRoot.addEventListener('change', this.rechargeWidgetRootListener, true);
    this.rechargeWidgetRoot.addEventListener('input', this.rechargeWidgetRootListener, true);

    this.rechargeWidgetObserver = new MutationObserver(() => {
      this.attachRechargePlanElementListener();
      this.syncRechargeQuantity();
    });

    this.rechargeWidgetObserver.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['value', 'checked', 'selected'],
    });
  }

  findRechargeWidgetHosts() {
    const widgets = Array.from(
      document.querySelectorAll('recharge-subscription-widget, rc-selling-plans, rc-subscription-widget')
    );
    if (!widgets.length) return [];

    const productId = this.getRechargeProductId();
    const variantId = this.getRechargeVariantId();

    if (productId) {
      const matches = widgets.filter((widget) => {
        const widgetProductId = widget.getAttribute('product-id') || widget.getAttribute('data-product-id');
        return widgetProductId === productId;
      });
      if (matches.length) return matches;
    }

    if (variantId) {
      const matches = widgets.filter(
        (widget) => widget.getAttribute('default-variant-id') === variantId
      );
      if (matches.length) return matches;
    }

    const scope =
      this.closest('[data-section-type="product-information"], .product__info-container, .product, .shopify-section') ||
      document;
    const scoped = Array.from(
      scope.querySelectorAll('recharge-subscription-widget, rc-selling-plans, rc-subscription-widget')
    );
    if (scoped.length) return scoped;

    return widgets;
  }

  getRechargeProductId() {
    if (this.rechargeForm) {
      const formProductId =
        this.rechargeForm.getAttribute('data-product-id') || this.rechargeForm.dataset?.productId;
      if (formProductId) return String(formProductId);

      const productInput = this.rechargeForm.querySelector('[name="product-id"]');
      if (productInput instanceof HTMLInputElement && productInput.value) {
        return String(productInput.value);
      }
    }

    const productForm = this.closest('product-form-component');
    if (productForm) {
      const productId =
        productForm.getAttribute('data-product-id') || productForm.dataset?.productId;
      if (productId) return String(productId);
    }

    return '';
  }

  getRechargeVariantId() {
    const variantId = this.getAttribute('data-variant-id');
    return variantId ? String(variantId) : '';
  }

  getRechargePlanId(form) {
    if (this.rechargePlanElement) {
      if (
        this.rechargePlanElement instanceof HTMLSelectElement ||
        this.rechargePlanElement instanceof HTMLInputElement
      ) {
        if (this.rechargePlanElement instanceof HTMLInputElement && this.rechargePlanElement.type === 'radio') {
          return this.rechargePlanElement.checked ? this.rechargePlanElement.value || '' : '';
        }

        return this.rechargePlanElement.value || '';
      }
    }

    if (!form) return '';

    const planElements = form.querySelectorAll('[name="selling_plan"], [name="rc_plan"]');
    if (!planElements.length) return '';

    for (const el of planElements) {
      if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement)) continue;
      if (el instanceof HTMLInputElement && el.type === 'radio') {
        if (el.checked) return el.value || '';
        continue;
      }
      return el.value || '';
    }

    return '';
  }

  syncRechargeQuantity() {
    const { quantityInput } = this.refs;
    const form = this.rechargeForm;

    this.attachRechargePlanElementListener();

    const formPlanId = form ? this.getRechargePlanId(form) : '';
    let planId = formPlanId;
    let planLabel = '';

    if (!planId && this.rechargePlanElement) {
      if (
        this.rechargePlanElement instanceof HTMLSelectElement ||
        this.rechargePlanElement instanceof HTMLInputElement
      ) {
        if (this.rechargePlanElement instanceof HTMLInputElement && this.rechargePlanElement.type === 'radio') {
          if (this.rechargePlanElement.checked) {
            planId = this.rechargePlanElement.value || '';
          }
        } else {
          planId = this.rechargePlanElement.value || '';
        }
      }
    }

    /** @type {Map<string, number> | null} */
    let optionQuantityMap = null;

    if (this.rechargePlanElement instanceof HTMLSelectElement) {
      optionQuantityMap = new Map();
      for (const option of Array.from(this.rechargePlanElement.options)) {
        const optionLabel = option.textContent || '';
        const parsed = parsePlanQuantityFromLabel(optionLabel);
        if (parsed) {
          optionQuantityMap.set(String(option.value), parsed);
        }
      }

      console.debug('[Recharge] optionQuantityMap', {
        planId,
        options: Array.from(this.rechargePlanElement.options).map((option) => ({
          value: String(option.value),
          label: (option.textContent || '').trim(),
          parsedQuantity: parsePlanQuantityFromLabel(option.textContent || ''),
        })),
        map: Array.from(optionQuantityMap.entries()),
      });

      const selectedOption = this.rechargePlanElement.selectedOptions?.[0];
      if (selectedOption) {
        planLabel = selectedOption.textContent || '';
      }
    } else if (this.rechargePlanElement instanceof HTMLElement) {
      planLabel = getPlanLabelFromElement(this.rechargePlanElement);
    }

    const targetQuantity =
      (optionQuantityMap?.get(String(planId)) ?? null) ??
      getRechargePlanQuantity(planId) ??
      parsePlanQuantityFromLabel(planLabel) ??
      parsePlanQuantityFromElement(this.rechargePlanElement);

    if (!targetQuantity) {
      const previous = quantityInput.getAttribute('data-recharge-previous-quantity');
      if (previous) {
        this.setValue(previous);
        quantityInput.removeAttribute('data-recharge-previous-quantity');
        if (this.onQuantityChange) {
          this.onQuantityChange();
        } else {
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      this.setLocked(false);
      return;
    }

    if (!this.isLocked()) {
      quantityInput.setAttribute('data-recharge-previous-quantity', quantityInput.value);
    }

    const clamped = clampQuantityToInput(quantityInput, targetQuantity);
    const value = String(clamped);

    if (quantityInput.value !== value) {
      this.setValue(value);
      if (this.onQuantityChange) {
        this.onQuantityChange();
      } else {
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    this.setLocked(true);
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
